import React, { useRef, useEffect, useState } from "react";
import { getTodayKeyForCache } from "../utils/gameUtils";

const THRESHOLD = 50;
const EDGE_GAIN = 0.85;

function sobelEdges(gray, w, h, gain) {
  const out = new Uint8ClampedArray(w * h);
  for (let y = 1; y < h - 1; y++) {
    const yw = y * w;
    for (let x = 1; x < w - 1; x++) {
      const i = yw + x;
      const a = gray[i - w - 1],
        b = gray[i - w],
        c = gray[i - w + 1];
      const d = gray[i - 1],
        f = gray[i + 1];
      const g = gray[i + w - 1],
        h2 = gray[i + w],
        k = gray[i + w + 1];
      const gx = -a + c + (-2 * d + 2 * f) + (-g + k);
      const gy = a + 2 * b + c + (-g - 2 * h2 - k);
      let mag = (Math.abs(gx) + Math.abs(gy)) * gain;
      if (mag > 255) mag = 255;
      out[i] = mag;
    }
  }
  return out;
}

function VideoStage({ videoUrl, showSketch, onLoaded }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const offCanvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const rafIdRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !videoUrl) return;

    const off = offCanvasRef.current || document.createElement("canvas");
    const offCtx = off.getContext("2d", { willReadFrequently: true });
    if (!offCtx) return;
    offCanvasRef.current = off;

    function ensureSketchSize() {
      const w = video.videoWidth | 0;
      const h = video.videoHeight | 0;
      if (!w || !h) return false;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        off.width = w;
        off.height = h;
      }
      return true;
    }

    function renderSketchFrame() {
      if (
        !showSketch ||
        video.paused ||
        video.ended ||
        !ensureSketchSize()
      )
        return;
      const w = off.width,
        h = off.height;
      offCtx.drawImage(video, 0, 0, w, h);
      const img = offCtx.getImageData(0, 0, w, h);
      const data = img.data;
      const gray = new Uint8ClampedArray(w * h);
      for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        const r = data[i],
          g = data[i + 1],
          b = data[i + 2];
        gray[p] = (0.2126 * r + 0.7152 * g + 0.0722 * b) | 0;
      }
      const edges = sobelEdges(gray, w, h, EDGE_GAIN);
      for (let p = 0, i = 0; p < edges.length; p++, i += 4) {
        const v = edges[p] >= THRESHOLD ? 0 : 255;
        data[i] = v;
        data[i + 1] = v;
        data[i + 2] = v;
        data[i + 3] = 255;
      }
      canvas.getContext("2d", { willReadFrequently: true }).putImageData(img, 0, 0);
    }

    function startLoop() {
      if ("requestVideoFrameCallback" in HTMLVideoElement.prototype) {
        const step = () => {
          renderSketchFrame();
          video.requestVideoFrameCallback(step);
        };
        video.requestVideoFrameCallback(step);
      } else {
        const raf = () => {
          rafIdRef.current = requestAnimationFrame(raf);
          renderSketchFrame();
        };
        raf();
      }
    }

    const handleLoadedMetadata = () => {
      setLoading(false);
      onLoaded?.();
      video.play().catch((err) => console.error("Playback error:", err));
      if (showSketch) startLoop();
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata, {
      once: true,
    });

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [videoUrl, showSketch, onLoaded]);

  useEffect(() => {
    if (!videoRef.current || !videoUrl) return;
    videoRef.current.crossOrigin = "anonymous";
    videoRef.current.loop = true;
    videoRef.current.muted = true;
    setLoading(true);
  }, [videoUrl]);

  useEffect(() => {
    if (!videoRef.current || !videoUrl) return;
    if (showSketch) {
      const today = getTodayKeyForCache();
      videoRef.current.src = `${videoUrl}?d=${today}`;
    } else {
      videoRef.current.src = videoUrl;
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [videoUrl, showSketch]);

  const displaySketch = showSketch;
  return (
    <div className="video-stage">
      <video
        ref={videoRef}
        width="100%"
        controls
        autoPlay
        loop
        muted
        playsInline
        style={{ visibility: displaySketch ? "hidden" : "visible" }}
      >
        <source src="" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <canvas
        ref={canvasRef}
        className="sketch-canvas"
        style={{
          display: displaySketch ? "block" : "none",
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          background: "#fff",
        }}
      />
      {loading && (
        <div className="video-loading">
          <div>
            <div className="video-loading-text">Loading video...</div>
            <div className="video-loading-sub">Please wait</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoStage;
